import { PROVINCES } from 'shared-components/src/utils/provinces';
import { isExemptBin } from 'shared-components/src/utils/binExemption';
import { digitsOnly } from './cardFormatting';

// Rules for the debit card contact dialogs, checked here rather than by the browser: the
// browser's own messages ("Please match the requested format.") follow the browser's
// language and never say what the field expects.

// Only Canada is accepted today.
export const CONTACT_COUNTRY = 'CAN';
export const provinceKeys = PROVINCES.map(p => p.key);

// "k1a0b1", "K1A-0B1" and "k1a 0b1" all become "K1A 0B1".
export const formatPostalCode = value => {
  const chars = String(value || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 6);
  return chars.length > 3 ? `${chars.slice(0, 3)} ${chars.slice(3)}` : chars;
};

// A stored province saved before the list existed ("on") opens selected; anything that is
// not on the list opens unselected rather than as a value the list cannot show.
export const toProvinceKey = value => {
  const key = String(value || '').trim().toUpperCase();
  return provinceKeys.includes(key) ? key : '';
};

// Expiry and CVV are asked for once the BIN is known (6 digits) and only when it is not exempt.
export const needsCardSecurity = cardNumber => {
  const digits = digitsOnly(cardNumber);
  return digits.length >= 6 && !isExemptBin(digits);
};

const CONTACT_NAME_REGEX = /^(?!.*\s{2,})[a-zA-Z0-9áàâäèéêëîïôœùûüúÿçÁÀÂÄÈÉÊËÎÏÔŒÙÛÜÚŸÇ\s-]{3,80}$/;
const EXPIRY_REGEX = /^(0[1-9]|1[0-2])\/\d{4}$/;
const POSTAL_CODE_REGEX = /^[A-Z]\d[A-Z] \d[A-Z]\d$/;

const required = (value, key) => (String(value || '').trim() ? '' : key);

// Returns { field: i18n key } for every field that fails. `strictName` keeps the Contacts
// page's existing 3-80 character rule on the contact name.
export const contactCardErrors = (values, { strictName = false } = {}) => {
  const errors = {
    name: required(values.name, 'contact-fields-errors-name-required'),
    fullName: required(values.fullName, 'contact-fields-errors-fullName-required'),
    addressLineOne: required(values.addressLineOne, 'contact-fields-errors-addressLineOne-required'),
    city: required(values.city, 'contact-fields-errors-city-required'),
    state: provinceKeys.includes(values.state) ? '' : 'contact-fields-errors-state-required',
  };

  if (!errors.name && strictName && !CONTACT_NAME_REGEX.test(values.name.trim())) {
    errors.name = 'contact-fields-errors-name-invalid';
  }

  const cardDigits = digitsOnly(values.cardNumber);
  if (!cardDigits) errors.cardNumber = 'contact-fields-errors-cardNumber-required';
  else if (cardDigits.length < 16 || cardDigits.length > 19) {
    errors.cardNumber = 'contact-fields-errors-cardNumber-invalid';
  }

  if (needsCardSecurity(values.cardNumber)) {
    if (!EXPIRY_REGEX.test(String(values.expiry || '').trim())) {
      errors.expiry = 'contact-fields-errors-expiry-invalid';
    }
    if (!/^\d{3}$/.test(String(values.cvv || '').trim())) {
      errors.cvv = 'contact-fields-errors-cvv-invalid';
    }
  }

  const postalCode = String(values.postalCode || '').trim();
  if (!postalCode) errors.postalCode = 'contact-fields-errors-postalCode-required';
  else if (!POSTAL_CODE_REGEX.test(formatPostalCode(postalCode))) {
    errors.postalCode = 'contact-fields-errors-postalCode-invalid';
  }

  return Object.fromEntries(Object.entries(errors).filter(([, error]) => error));
};

// The same address check the Interac e-Transfer contact dialog already used.
const EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

const personName = (value, field) => {
  const name = String(value || '').trim();
  if (!name) return `contact-fields-errors-${field}-required`;
  return name.includes('@') ? `contact-fields-errors-${field}-invalid` : '';
};

// Returns { field: i18n key } for every e-Transfer contact field that fails.
export const etransferContactErrors = values => {
  const name = String(values.name || '').trim();
  const email = String(values.email || '').trim();
  const confirmEmail = String(values.confirmEmail || '').trim();

  const errors = {
    firstName: personName(values.firstName, 'firstName'),
    lastName: personName(values.lastName, 'lastName'),
  };

  if (!name) errors.name = 'contact-fields-errors-name-required';
  else if (!CONTACT_NAME_REGEX.test(name)) errors.name = 'contact-fields-errors-name-invalid';

  if (!email) errors.email = 'contact-fields-errors-email-required';
  // At least 10 characters, as the email inputs' minLength asked before.
  else if (email.length < 10 || !EMAIL_REGEX.test(email)) errors.email = 'contact-fields-errors-email-invalid';

  if (!confirmEmail) errors.confirmEmail = 'contact-fields-errors-confirmEmail-required';
  else if (confirmEmail !== email) errors.confirmEmail = 'contact-fields-errors-confirmEmail-mismatch';

  return Object.fromEntries(Object.entries(errors).filter(([, error]) => error));
};
